import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loader2, Play, ArrowRight, ArrowLeft, Cast } from "lucide-react";
import { postTimeJump } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import TVCastOverlay from "@/components/TVCastOverlay";
import ShareButtons from "@/components/ShareButtons";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * HistoryPage — SEO-focused static page for `/history/:slug`.
 *
 * Slug format: YYYY-MM-DD-{REGION}  (e.g. "1985-07-13-GB")
 *
 * Purpose
 * -------
 * A Google-indexable landing page for each historical date. Renders the full
 * snapshot content as visible plain text (news headlines, weather, music,
 * movies) so search engines can index it. Also stamps `<title>`, meta
 * description, Open Graph tags, and a NewsArticle JSON-LD block for rich
 * search results.
 *
 * The primary CTA points to `/watch/{id}` (Cinema Mode) — this is the funnel
 * from free organic search traffic → premium conversions.
 */
export default function HistoryPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const [castOpen, setCastOpen] = useState(false);

  const parsed = useMemo(() => {
    // slug is YYYY-MM-DD-REGION → split on the 4th "-"
    const m = slug.match(/^(\d{4}-\d{2}-\d{2})-([A-Za-z]{2})$/);
    if (!m) return null;
    return { date: m[1], region: m[2].toUpperCase() };
  }, [slug]);

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!parsed) {
      setError("Invalid history URL. Try /history/1985-07-13-GB");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await postTimeJump({ date: parsed.date, region: parsed.region });
        if (cancelled) return;
        setDoc(data);
        trackEvent("history_seo_view", { date: parsed.date, region: parsed.region });
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load this date");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed?.date, parsed?.region]);

  // Update meta tags + JSON-LD once the snapshot loads.
  useEffect(() => {
    if (!doc) return;
    const meta = doc.snapshot?._meta || {};
    const region = meta.region?.name || parsed.region;
    const dateLong = meta.long_date || doc.date;
    const news = doc.snapshot?.news || [];
    const firstHeadline = news[0]?.headline || `A retro daily snapshot from ${region}`;

    const title = `What happened on ${dateLong} in ${region}? · TimeCapsule 24`;
    const description = `${firstHeadline}. Explore news, weather, music, and pop culture from ${dateLong} in ${region} on TimeCapsule 24.`;
    const canonicalUrl = `${window.location.origin}/history/${doc.date}-${(doc.region || parsed.region).toUpperCase()}`;
    const ogImage = `${BACKEND_URL}/api/og/${doc.id}.png`;

    document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        // parse selector like [name="foo"] → set name/property attribute
        const m = selector.match(/\[(name|property)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", description);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:image"]', "content", ogImage);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", description);
    setMeta('meta[name="twitter:image"]', "content", ogImage);

    // Canonical link
    let canon = document.querySelector('link[rel="canonical"]');
    if (!canon) {
      canon = document.createElement("link");
      canon.setAttribute("rel", "canonical");
      document.head.appendChild(canon);
    }
    canon.setAttribute("href", canonicalUrl);

    // JSON-LD structured data for search engines
    const jsonld = {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: title,
      description,
      datePublished: doc.date,
      author: { "@type": "Organization", name: "TimeCapsule 24" },
      publisher: {
        "@type": "Organization",
        name: "TimeCapsule 24",
        logo: { "@type": "ImageObject", url: `${window.location.origin}/favicon.ico` },
      },
      image: [ogImage],
      mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
    };
    let ld = document.getElementById("ld-history");
    if (!ld) {
      ld = document.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.id = "ld-history";
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(jsonld);

    return () => {
      // On unmount, drop the JSON-LD block so it doesn't linger on other routes.
      const el = document.getElementById("ld-history");
      if (el) el.remove();
    };
  }, [doc, parsed]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin mx-auto mb-3 text-press-red" />
          <div className="font-mono uppercase tracking-widest text-xs text-ink-muted">
            Loading the archives…
          </div>
        </div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <div className="ink-stamp red mb-3">Not found</div>
        <div className="font-headline uppercase text-3xl sm:text-4xl font-black tracking-tighter">
          {error || "This page couldn't be loaded."}
        </div>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 border-2 border-ink px-5 py-3 font-mono uppercase text-xs tracking-widest hover:bg-ink hover:text-paper"
        >
          <ArrowLeft size={14} /> Back home
        </Link>
      </div>
    );
  }

  const s = doc.snapshot || {};
  const meta = s._meta || {};
  const news = s.news || [];
  const weather = s.weather || {};
  const music = s.music || {};
  const topSongs = Array.isArray(music) ? music : (music.top_songs || []);
  const cinema = s.cinema || s.movies || {};
  const movies = Array.isArray(cinema) ? cinema : (cinema.top_movies || []);
  const flag = meta.region?.flag || "";
  const region = meta.region?.name || doc.region;
  const dateLong = meta.long_date || doc.date;

  return (
    <div className="min-h-screen paper-grain" data-testid="history-page">
      {/* Top nav */}
      <div className="border-b-2 border-ink px-6 sm:px-10 py-4 flex items-center justify-between bg-paper-alt">
        <Link
          to="/"
          className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs hover:text-press-red"
          data-testid="history-nav-home"
        >
          ← TimeCapsule 24
        </Link>
        <div className="flex items-center gap-2">
          <ShareButtons
            url={typeof window !== "undefined" ? window.location.href : ""}
            title={typeof document !== "undefined" ? document.title : "TimeCapsule 24"}
            text={((doc?.snapshot?.news || [])[0]?.headline) || `A retro daily snapshot from TimeCapsule 24`}
            eventName="history_share"
          />
          <button
            onClick={() => setCastOpen(true)}
            className="inline-flex items-center gap-1.5 border-2 border-ink px-3 py-2 font-mono uppercase tracking-widest text-[10px] sm:text-xs hover:bg-ink hover:text-paper"
            data-testid="history-cast-btn"
            title="Cast to another screen"
          >
            <Cast size={12} /> <span className="hidden sm:inline">Cast</span>
          </button>
          <button
            onClick={() => { trackEvent("cinema_open", { snapshot_id: doc.id, source: "history" }); navigate(`/watch/${doc.id}`); }}
            className="inline-flex items-center gap-2 bg-press-red text-paper border-2 border-press-red px-4 py-2 font-mono uppercase tracking-widest text-[10px] sm:text-xs hover:bg-ink hover:border-ink"
            data-testid="history-watch-btn"
          >
            <Play size={12} /> Watch
          </button>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-16">
        <div className="font-mono uppercase tracking-[0.35em] text-[10px] sm:text-xs text-press-red mb-4">
          On this day · {flag} {region}
        </div>
        <h1
          className="font-headline uppercase font-black leading-[0.95] tracking-tighter text-3xl sm:text-5xl lg:text-6xl"
          data-testid="history-h1"
        >
          What happened on {dateLong} in {region}?
        </h1>
        {news[0] && (
          <p className="mt-6 font-body italic text-lg sm:text-xl text-ink-muted max-w-2xl">
            {news[0].headline}. {news[0].body?.slice(0, 240) || ""}
          </p>
        )}

        {/* News */}
        {news.length > 0 && (
          <section className="mt-12">
            <h2 className="font-headline uppercase font-black text-2xl sm:text-3xl tracking-tight border-b-2 border-ink pb-2">
              Headlines
            </h2>
            <div className="mt-4 space-y-6">
              {news.slice(0, 3).map((n, i) => (
                <div key={i} data-testid={`history-news-${i}`}>
                  <h3 className="font-headline font-bold text-lg sm:text-xl">{n.headline}</h3>
                  {n.body && (
                    <p className="mt-1 font-body text-sm sm:text-base text-ink-muted">
                      {n.body}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Weather */}
        {weather?.condition && (
          <section className="mt-10">
            <h2 className="font-headline uppercase font-black text-xl sm:text-2xl tracking-tight border-b-2 border-ink pb-2">
              Weather
            </h2>
            <p className="mt-3 font-body text-base">
              {weather.condition}
              {weather.high_c != null && ` · high ${weather.high_c}°C`}
              {weather.high_f != null && weather.high_c == null && ` · high ${weather.high_f}°F`}
              {weather.city && ` in ${weather.city}`}.
              {weather.notes && ` ${weather.notes}`}
            </p>
          </section>
        )}

        {/* Music */}
        {topSongs.length > 0 && (
          <section className="mt-10">
            <h2 className="font-headline uppercase font-black text-xl sm:text-2xl tracking-tight border-b-2 border-ink pb-2">
              On the radio
            </h2>
            <ol className="mt-3 space-y-1 font-body text-base">
              {topSongs.slice(0, 5).map((t, i) => (
                <li key={i} data-testid={`history-song-${i}`}>
                  <span className="font-mono text-press-red mr-2">#{t.rank || i + 1}</span>
                  <span className="font-semibold">{t.title}</span> — {t.artist}
                </li>
              ))}
            </ol>
          </section>
        )}

        {/* Cinema */}
        {movies.length > 0 && (
          <section className="mt-10">
            <h2 className="font-headline uppercase font-black text-xl sm:text-2xl tracking-tight border-b-2 border-ink pb-2">
              At the picture-house
            </h2>
            <ul className="mt-3 space-y-2 font-body text-base">
              {movies.slice(0, 3).map((m, i) => (
                <li key={i} data-testid={`history-movie-${i}`}>
                  <span className="font-semibold">{m.title}</span>
                  {m.note && <span className="text-ink-muted"> — {m.note}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Big CTA — this is the funnel */}
        <section className="mt-16 border-2 border-ink bg-paper-alt p-6 sm:p-10 text-center">
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-press-red mb-3">
            ✦ Experience it fully
          </div>
          <h2 className="font-headline uppercase font-black text-2xl sm:text-4xl tracking-tighter">
            Watch {dateLong} come alive.
          </h2>
          <p className="mt-3 font-body italic text-ink-muted max-w-lg mx-auto">
            Full-screen photos, AI narration, and the era's music — three minutes of history you can lean back and watch.
          </p>
          <button
            onClick={() => { trackEvent("cinema_open", { snapshot_id: doc.id, source: "history_cta" }); navigate(`/watch/${doc.id}`); }}
            className="mt-6 inline-flex items-center gap-2 bg-press-red text-paper border-2 border-press-red px-6 py-4 font-mono uppercase text-xs sm:text-sm tracking-widest hover:bg-ink hover:border-ink"
            data-testid="history-cta-watch"
          >
            <Play size={14} /> Play the episode <ArrowRight size={14} />
          </button>
          <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
            Free · 2 Cinema Mode plays per day
          </div>
        </section>

        {/* Related dates (simple internal linking for SEO) */}
        <section className="mt-16">
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-press-red mb-3">
            Explore other days
          </div>
          <div className="flex flex-wrap gap-2">
            {[
              { d: "1969-07-20", r: "US", label: "Moon Landing" },
              { d: "1989-11-09", r: "DE", label: "Berlin Wall" },
              { d: "1985-07-13", r: "GB", label: "Live Aid" },
              { d: "2001-09-11", r: "US", label: "9/11" },
              { d: "1981-10-06", r: "EG", label: "Sadat" },
            ].filter(x => `${x.d}-${x.r}` !== `${doc.date}-${doc.region}`).map((x) => (
              <Link
                key={`${x.d}-${x.r}`}
                to={`/history/${x.d}-${x.r}`}
                className="inline-flex items-center gap-1.5 border-2 border-ink px-3 py-1.5 font-mono uppercase tracking-widest text-[10px] hover:bg-ink hover:text-paper"
              >
                {x.label} · {x.d}
              </Link>
            ))}
          </div>
        </section>
      </article>

      <footer className="border-t-2 border-ink px-6 sm:px-10 py-6 mt-10 text-center bg-paper-alt">
        <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-ink-muted">
          TimeCapsule 24 · A daily snapshot machine for the last century.
        </div>
        <div className="mt-2 flex items-center justify-center gap-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          <Link to="/contact" className="hover:text-press-red" data-testid="footer-contact-link">Contact</Link>
          <span aria-hidden="true">·</span>
          <a href="mailto:support@timecapsule24.tv" className="hover:text-press-red">support@timecapsule24.tv</a>
        </div>
      </footer>

      <TVCastOverlay
        open={castOpen}
        onClose={() => setCastOpen(false)}
        url={typeof window !== "undefined" ? window.location.href : ""}
        snapshotId={doc?.id}
      />
    </div>
  );
}
